import { Injectable } from '@angular/core';
import { MatSnackBar } from '@angular/material/snack-bar';
import * as FileSaver from 'file-saver';
import * as SparkMD5 from 'spark-md5';
import { CompanySubjectPair, FileVaultCategory, FileVaultReference, FileVaultReferenceRelation, FileVaultSubjectType, FileVaultUploadCommitRequest, FileVaultUploadRequest, RefreshFileVaultUploadRequest, RemoveFileVaultReferencesRequest, RemoveFileVaultReferencesResponse, SituViewWebApiClient } from 'situview-webapi-client';
import { ConfirmDialogData } from '../components/confirm-dialog/ConfirmDialogData';
import { ConfigService } from './config.service';
import { ConfirmDialogService } from './confirm-service';
import { FileVaultUpload, RefreshSasUrl, UPLOAD_BLOCK_SIZE, UploadProgressHandler } from './file-vault-upload';

@Injectable(
  { providedIn: 'root' }
)
export class FileVaultService {
  private situViewUrl;

  constructor(
    configService: ConfigService,
    private readonly snackbar: MatSnackBar,
    private readonly confirmDialogService: ConfirmDialogService,
    private readonly situViewClient: SituViewWebApiClient) {
    this.situViewUrl = configService.config.situViewWebApiBaseUrl;
  }

  private showMessage(message: string) {
    this.snackbar.open(message, 'OK', { duration: 2000 });
  }

  public getFileDownloadUrl(file: FileVaultReferenceRelation) {
    if (file) {
      var url = `${this.situViewUrl}/api/commands/experimental/FileVault/GetFile?companyId=${file.companyId}&fileVaultReferenceId=${file.id}&fileVaultCategory=${FileVaultCategory.MarketingMaterial}`;
      return url;
    }
    return '';
  }

  public async download(file: FileVaultReferenceRelation) {
    var fileResponse = await this.situViewClient.getFile(file.companyId, file.id, FileVaultCategory.MarketingMaterial);

    if (fileResponse.status !== 200) {
      this.showMessage("Failed to load download file");
      return;
    }
    FileSaver.saveAs(fileResponse.data, file.fileName);
  }

  public async uploadBlob(
    companyId: string, 
    subjectId: string, 
    subjectType: FileVaultSubjectType, 
    category: FileVaultCategory,
    fileDetails: File, 
    path?: string): Promise<FileVaultReferenceRelation | undefined> {

    try {
      const fileVaultUploadRequest = new FileVaultUploadRequest();
      fileVaultUploadRequest.companyId = companyId;
      fileVaultUploadRequest.subjectId = subjectId;
      fileVaultUploadRequest.subjectType = subjectType;
      fileVaultUploadRequest.fileName = fileDetails.name;
      fileVaultUploadRequest.category = category;
      fileVaultUploadRequest.filePath = path;
      fileVaultUploadRequest.fileHash = await this.computeFileMd5Hex(fileDetails);
      // This path always uploads a new file; the hash is still stored so other uploads can dedup against it.
      fileVaultUploadRequest.allowDuplicate = true;

      const requestUploadResponse = await this.situViewClient.requestFileVaultUpload(fileVaultUploadRequest);

      if (!requestUploadResponse.success) {
        let msg = requestUploadResponse.errorMessages?.find(_ => true);
        if (msg) {
          msg = `Failed to request file upload: ${msg}`;
        } else {
          msg = "Failed to request file upload.";
        }

        throw new Error(msg);
      }

      const upload = this.createUpload(
        fileDetails,
        requestUploadResponse.uploadUrl!,
        () => this.refreshUploadUrl(companyId, requestUploadResponse.fileVaultReferenceId!, category),
      );
      await upload.upload();

      const fileVaultUploadCommitRequest = new FileVaultUploadCommitRequest();
      fileVaultUploadCommitRequest.companyId = companyId;
      fileVaultUploadCommitRequest.fileVaultReferenceId = requestUploadResponse.fileVaultReferenceId;
      fileVaultUploadCommitRequest.category = category;
      const uploadCommitResponse = await this.situViewClient.commitFileVaultUpload(fileVaultUploadCommitRequest);

      if (!uploadCommitResponse.success) {
        // Upload completed
        throw new Error("Failed to commit file.");
      }

      return uploadCommitResponse.fileVaultReferenceRelation;

    } catch (e: any) {
      this.showMessage(e.message);
    }

  }

  // Creates an upload for a file against a blob SAS URL. The optional refresh callback is used
  // to obtain a new SAS if the current one expires mid-upload, and callers can retry failed
  // chunks by calling upload() again on the returned object.
  public createUpload(file: File, sasUrl: string, refreshSasUrl?: RefreshSasUrl): FileVaultUpload {
    return new FileVaultUpload(file, sasUrl, refreshSasUrl);
  }

  // Convenience one-shot upload for callers that don't need retry control.
  public async uploadToSasUrl(sasUrl: string, file: File, onProgress?: UploadProgressHandler): Promise<void> {
    await this.createUpload(file, sasUrl).upload(onProgress);
  }

  // Requests a fresh upload SAS for an uncommitted file so it can be re-uploaded.
  public async refreshUploadUrl(companyId: string | undefined, fileVaultReferenceId: string | undefined, category: FileVaultCategory | undefined): Promise<string> {
    const request = new RefreshFileVaultUploadRequest();
    request.companyId = companyId;
    request.fileVaultReferenceId = fileVaultReferenceId;
    request.category = category;

    const response = await this.situViewClient.refreshFileVaultUpload(request);
    if (!response.success || !response.uploadUrl) {
      throw new Error('Failed to refresh the upload link.');
    }
    return response.uploadUrl;
  }

  // Computes the file's MD5 as a lowercase hex string, reading it in blocks to avoid
  // loading the whole file into memory. Sent with the upload request as the file hash.
  public async computeFileMd5Hex(file: File): Promise<string> {
    const spark = new SparkMD5.ArrayBuffer();

    for (let start = 0; start < file.size; start += UPLOAD_BLOCK_SIZE) {
      const buffer = await file.slice(start, Math.min(start + UPLOAD_BLOCK_SIZE, file.size)).arrayBuffer();
      spark.append(buffer);
    }

    return spark.end();
  }

  // Requesting an upload stamps the updated date, so a file that has never been replaced carries one
  // a moment after its created date. Only a gap wider than this counts as a replacement.
  private static readonly replacedAfterMs = 60 * 1000;

  public hasBeenReplaced(file: FileVaultReference): boolean {
    if (!file?.createdDate || !file?.updatedDate) {
      return false;
    }

    return file.updatedDate.getTime() - file.createdDate.getTime() > FileVaultService.replacedAfterMs;
  }

  public async confirmRemoveFile(companyId: string, subjectId: string, file: FileVaultReferenceRelation): Promise<RemoveFileVaultReferencesResponse | undefined> {

    this.confirmDialogService.open({
      title: 'Remove File',
      message: `Are you sure you wish to remove '${file.fileName}' permanently?`,
      cancelText: 'No',
      confirmText: 'Yes',
      actionIcon: 'delete',
      actionColour: 'warn'
    } as ConfirmDialogData);

    let confirmed = await this.confirmDialogService.confirmed().toPromise();
    if (confirmed) {
      return await this.removeFileRequest(companyId, subjectId, file);
    }
  }

  public async removeFileRequest(companyId: string, subjectId: string, file: FileVaultReferenceRelation): Promise<RemoveFileVaultReferencesResponse> {
    var removeReferencesRequest = new RemoveFileVaultReferencesRequest();
    removeReferencesRequest.fileVaultReferenceId = file.id;
    removeReferencesRequest.category = file.category;
    removeReferencesRequest.force = true;

    var companySubjectPair = new CompanySubjectPair;
    companySubjectPair.companyId = companyId;
    companySubjectPair.subjectId = subjectId;
    removeReferencesRequest.subjects = [companySubjectPair];

    let response = await this.situViewClient.removeFileVaultReferences(removeReferencesRequest);
    if (!response.success) {
      this.showMessage("Failed to remove file from company");
    }
    else {
      this.showMessage(`Removed file from company`);
    }
    return response;
  }
}
